import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Siren, Plus, Trash2, Pencil, Check, Phone, X } from "lucide-react";
import { formatDateES } from "@/lib/utils-app";

// V1.50 · Módulo Ciclo DGT (CAPA/CIR): hitos libres con nº de ciclo opcional
// + lista indefinida de teléfonos WhatsApp. Visible para matriz/franquicia y
// profesores con permiso `ciclo_dgt` (p. ej. una secretaria).
export const CicloDgtPanel = ({ onChanged }) => {
  const { user } = useAuth();
  const isTeacher = String(user?.role || "").toLowerCase() === "teacher";
  const canSee = !isTeacher || !!user?.permissions?.ciclo_dgt;

  const [milestones, setMilestones] = useState([]);
  const [phones, setPhones] = useState([]);
  const [form, setForm] = useState({ date: "", title: "", ciclo_number: "" });
  const [newPhone, setNewPhone] = useState("");
  const [editingNum, setEditingNum] = useState(null); // {id, value}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/ciclo-milestones"),
        api.get("/ciclo-whatsapp-phones"),
      ]);
      setMilestones(m.data || []);
      setPhones(p.data?.phones || []);
    } catch (_) { /* sin permiso */ }
  }, []);

  useEffect(() => { if (canSee) load(); }, [canSee, load]);

  if (!canSee) return null;

  const addMilestone = async (e) => {
    e.preventDefault();
    if (!form.date || !form.title.trim()) {
      toast.error("Indica fecha y descripción del hito");
      return;
    }
    setBusy(true);
    try {
      await api.post("/ciclo-milestones", form);
      toast.success("Hito del ciclo registrado · alerta activa desde las 18:00 del día anterior");
      setForm({ date: "", title: "", ciclo_number: "" });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const removeMilestone = async (id) => {
    try {
      await api.delete(`/ciclo-milestones/${id}`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const saveCicloNumber = async (id, value) => {
    try {
      await api.patch(`/ciclo-milestones/${id}`, { ciclo_number: value });
      setEditingNum(null);
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const savePhones = async (next) => {
    try {
      const { data } = await api.put("/ciclo-whatsapp-phones", { phones: next });
      setPhones(data.phones);
      setNewPhone("");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const upcoming = milestones.filter((m) => m.date >= todayIso);
  const past = milestones.filter((m) => m.date < todayIso);

  return (
    <div data-testid="ciclo-dgt-panel" className="rounded-xl border-2 border-red-300 bg-red-50/40 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white">
        <Siren className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-wider">Ciclo DGT · Alertas supremas (CAPA / CIR)</span>
        <span className="ml-auto text-[10px] opacity-80 hidden sm:inline">Aviso desde las 18:00 del día anterior · evento a las 07:00 en calendario</span>
      </div>
      <div className="p-3 space-y-3">
        <form onSubmit={addMilestone} className="grid grid-cols-1 sm:grid-cols-[150px_1fr_130px_auto] gap-2">
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="ciclo-date-input"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Hito · ej: Confirmar cantidad CAPA / Presentación telemática CIR"
            data-testid="ciclo-title-input"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <input
            value={form.ciclo_number}
            onChange={(e) => setForm({ ...form, ciclo_number: e.target.value })}
            placeholder="Nº ciclo (opcional)"
            data-testid="ciclo-number-input"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="ciclo-add-button"
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 hover:bg-red-700 text-white px-3 h-9 text-xs font-bold"
          >
            <Plus className="h-3.5 w-3.5" /> Añadir hito
          </button>
        </form>

        {upcoming.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-white divide-y divide-red-100">
            {upcoming.map((m) => (
              <div key={m.id} data-testid={`ciclo-row-${m.id}`} className="flex items-center gap-2 px-3 py-2">
                <span className="text-xs font-bold text-red-700 capitalize w-32 shrink-0">{formatDateES(m.date)}</span>
                <span className="text-xs text-neutral-800 flex-1 min-w-0 truncate font-semibold">{m.title}</span>
                {editingNum?.id === m.id ? (
                  <span className="flex items-center gap-1 shrink-0">
                    <input
                      value={editingNum.value}
                      onChange={(e) => setEditingNum({ id: m.id, value: e.target.value })}
                      placeholder="Nº"
                      autoFocus
                      data-testid={`ciclo-num-edit-${m.id}`}
                      className="w-20 rounded-md border border-red-300 px-1.5 h-7 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <button type="button" onClick={() => saveCicloNumber(m.id, editingNum.value)} data-testid={`ciclo-num-save-${m.id}`} className="text-emerald-600 hover:text-emerald-800"><Check className="h-4 w-4" /></button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingNum({ id: m.id, value: m.ciclo_number || "" })}
                    data-testid={`ciclo-num-chip-${m.id}`}
                    className={
                      "shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold " +
                      (m.ciclo_number ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800 italic")
                    }
                    title="Editar nº de ciclo"
                  >
                    {m.ciclo_number ? `Ciclo ${m.ciclo_number}` : "nº pendiente"}
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                )}
                <span className="text-[10px] text-neutral-400 font-mono shrink-0 hidden sm:inline">límite {m.time_limit || "23:59"}</span>
                <button type="button" onClick={() => removeMilestone(m.id)} data-testid={`ciclo-delete-${m.id}`} className="text-neutral-400 hover:text-red-600 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        {upcoming.length === 0 && (
          <p className="text-[11px] text-neutral-500 italic px-1">Sin hitos de ciclo próximos. Añade las fechas en cuanto la Jefatura publique el ciclo.</p>
        )}
        {past.length > 0 && (
          <p className="text-[10px] text-neutral-400 px-1">{past.length} hito(s) pasados en el historial.</p>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-red-100">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-neutral-600">
            <Phone className="h-3 w-3" /> WhatsApp de aviso:
          </span>
          {phones.map((p) => (
            <span key={p} data-testid={`ciclo-phone-${p}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-0.5 text-[11px] font-mono font-semibold">
              {p}
              <button type="button" onClick={() => savePhones(phones.filter((x) => x !== p))} data-testid={`ciclo-phone-del-${p}`} className="text-emerald-600 hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
          <span className="inline-flex items-center gap-1">
            <input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+34XXXXXXXXX"
              data-testid="ciclo-phone-input"
              className="w-32 rounded-lg border border-neutral-300 bg-white px-2 h-7 text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => newPhone.trim() && savePhones([...phones, newPhone.trim()])}
              data-testid="ciclo-phone-add"
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              title="Añadir teléfono"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CicloDgtPanel;
